import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useEffect, useLayoutEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import MapView, { Region } from 'react-native-maps';

import { useGetUserImpact, useGetUserImpactMap } from '@/api/hooks/donationHooks';
import { Impact as ImpactType } from '@/api/models/impactModels';
import { Impact } from '@/api/models/impactModels';
import { bs_icon } from '@/assets/images';
import GoBackButton from '@/components/buttons/GoBackButton';
import ImpactMarker from '@/components/impact/ImpactMarker';
import ImpactPopUp from '@/components/impact/ImpactPopUp';
import TotalImpact from '@/components/impact/TotalImpact';
import Title from '@/components/Title';
import useGetLocation from '@/hooks/useGetLocation';
import { firebaseImage, safeJsonParseArray, ThumbSize } from '@/utils/firebaseImage';
import { getMapCorners } from '@/utils/getMapCorners';
import { i18n } from '@/utils/i18n';

const Map = () => {
  const navigation = useNavigation();
  const { npoType } = useLocalSearchParams<{ npoType: 'charity' | 'church' }>();

  const location = useGetLocation();

  const { data } = useGetUserImpact({ npoType: npoType, periodTime: 'all' });
  const { mutateAsync: getMapData } = useGetUserImpactMap();

  const [donations, setDonations] = useState<ImpactType[]>([]);
  const [showDonation, setShowDonation] = useState<boolean>(false);
  const [currentDonation, setCurrentDonation] = useState<Impact | null>(null);

  //Locales
  const charities_title = i18n.t('home.charities');
  const churches_title = i18n.t('home.churches');
  const total_allTime = i18n.t('profile.impact.total_allTime_impact');
  const titleScreen = npoType === 'charity' ? charities_title : churches_title;

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: true,
      headerShadowVisible: false,
      headerBackVisible: false,
      headerTitle: () => <Title label={titleScreen} />,
      headerLeft: () => <GoBackButton />
    });
  }, [npoType, navigation, titleScreen]);

  useEffect(() => {
    getMapData(
      { polygon: getMapCorners(initialRegion), npoType: npoType },
      {
        onSuccess: (response) => {
          setDonations(
            response.map<ImpactType>((donation, index) => ({
              id: index.toString(),
              amount: donation.npoTotalAmount,
              organization: donation.npoName,
              thumbnail:
                donation.thumbnail !== undefined
                  ? firebaseImage(safeJsonParseArray(donation.thumbnail ?? '[]').at(0), ThumbSize.Small)
                  : bs_icon,
              latitude: donation.location.coordinates[0],
              longitude: donation.location.coordinates[1]
            }))
          );
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create an initialRegion based on user's location
  const initialRegion: Region = {
    latitude: location?.latitude ?? 37.0902, //USA center
    longitude: location?.longitude ?? -95.7129, //USA center
    latitudeDelta: 40,
    longitudeDelta: 40
  };

  const handleDonationPress = (item: Impact) => {
    setCurrentDonation(item);
    setShowDonation(true);
  };

  return (
    <View style={styles.container}>
      <TotalImpact text={total_allTime} amount={data?.totalAmount ?? 0} />
      {currentDonation && (
        <ImpactPopUp data={currentDonation} showPopUp={showDonation} dismissPopUp={() => setShowDonation(false)} />
      )}
      <MapView
        style={styles.map}
        initialRegion={initialRegion}
        onRegionChangeComplete={(region) => {
          getMapData(
            { polygon: getMapCorners(region), npoType: npoType },
            {
              onSuccess: (response) => {
                setDonations(
                  response.map<ImpactType>((donation, index) => ({
                    id: index.toString(),
                    amount: donation.npoTotalAmount,
                    organization: donation.npoName,
                    thumbnail:
                      donation.thumbnail !== undefined
                        ? firebaseImage(safeJsonParseArray(donation.thumbnail ?? '[]').at(0), ThumbSize.Small)
                        : bs_icon,
                    latitude: donation.location.coordinates[0],
                    longitude: donation.location.coordinates[1]
                  }))
                );
              }
            }
          );
        }}>
        {donations.map((item) => {
          return <ImpactMarker key={item.id} data={item} showDonation={() => handleDonationPress(item)} />;
        })}
      </MapView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1
  },

  map: {
    flex: 1
  },

  backBtn: {
    marginLeft: 18
  }
});

export default Map;
